/**
 * Custom hook for handling building placement & build menu keyboard shortcuts.
 */

import { useEffect } from 'react';
import { useBuildModeStore } from '@shared/stores/buildModeStore';
import { useUnitSelectionStore } from '@shared/stores';
import { BuildingType } from '@shared/constants/buildings';
import { UnitType } from '@shared/constants/units';

interface UseBuildingKeyboardShortcutsOptions {
  scene: unknown | null;
}

/**
 * Hook for handling keyboard shortcuts for building placement and the build menu.
 *
 * - 'B' toggles the build menu when a single Builder is selected
 * - From the build menu:
 *   - 'T' opens the Tower submenu
 *   - 'W' selects Wall
 *   - 'B' selects Barracks
 *   - 'H' selects Hero Spawner
 *   - 'E' selects Tech Building
 *   - 'M' selects Mine
 * - From the Tower submenu:
 *   - 'A' selects Archer Tower
 *   - 'C' selects Cannon Tower
 *   - 'G' selects Magic Tower
 * - 'Escape' works in layers:
 *   - If in tower submenu → go back to main build menu
 *   - If in build mode → deactivate build mode
 *   - If in build menu → close build menu
 *   - If units selected → deselect units
 */
export function useBuildingKeyboardShortcuts({ scene }: UseBuildingKeyboardShortcutsOptions) {
  const {
    isBuildModeActive,
    isBuildMenuOpen,
    isTowerSubmenuOpen,
    activateBuildMode,
    deactivateBuildMode,
    closeBuildMenu,
    toggleBuildMenu,
    openTowerSubmenu,
    closeTowerSubmenu,
  } = useBuildModeStore();

  const selectedUnitIds = useUnitSelectionStore((state) => state.selectedUnitIds);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Only handle if not typing in an input field
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
        return;
      }

      // Escape key works in layers - one level at a time
      if (event.key === 'Escape') {
        event.preventDefault();

        // Layer 1: If tower submenu is open, go back to main build menu
        if (isTowerSubmenuOpen) {
          closeTowerSubmenu();
          return;
        }

        // Layer 2: If build mode is active, deactivate it
        if (isBuildModeActive) {
          deactivateBuildMode();
          return;
        }

        // Layer 3: If build menu is open, close it
        if (isBuildMenuOpen) {
          closeBuildMenu();
          return;
        }

        // Layer 4: If units are selected, deselect them via Phaser controller
        // This ensures both the visual indicators and the store are updated
        if (selectedUnitIds.length > 0 && scene) {
          const sceneWithSelection = scene as {
            getUnitSelectionController?: () => {
              clearSelection: () => void;
            } | null;
          };
          const selectionController = sceneWithSelection.getUnitSelectionController?.();
          if (selectionController) {
            selectionController.clearSelection();
          }
          return;
        }

        return;
      }

      // The rest of the shortcuts only apply when we have a single Builder selected
      const currentSelectedUnits = useUnitSelectionStore.getState().getSelectedUnits();
      if (!scene || typeof scene !== 'object' || scene === null) return;
      if (currentSelectedUnits.length !== 1) return;

      const sceneWithMethods = scene as {
        getUnitManager?: () => {
          getUnit: (id: string) =>
            | {
                getUnitType: () => UnitType;
              }
            | undefined;
        } | null;
      };

      const unitManager = sceneWithMethods.getUnitManager?.();
      if (!unitManager) return;

      const unit = unitManager.getUnit(currentSelectedUnits[0]);
      if (!unit || unit.getUnitType() !== UnitType.BUILDER) {
        return;
      }

      const key = event.key.toLowerCase();

      // 'b' toggles the build menu (only if menu is not already open)
      // If menu is open, 'b' should select Barracks instead
      if (key === 'b' && !isTowerSubmenuOpen && !isBuildMenuOpen) {
        event.preventDefault();
        toggleBuildMenu();
        return;
      }

      // The remaining keys only apply if the build menu is open
      if (!isBuildMenuOpen) {
        return;
      }

      // Tower submenu toggling with 't'
      if (key === 't') {
        event.preventDefault();
        if (isTowerSubmenuOpen) {
          closeTowerSubmenu();
        } else {
          openTowerSubmenu();
        }
        return;
      }

      // If tower submenu is open, handle tower-type hotkeys
      if (isTowerSubmenuOpen) {
        if (key === 'a') {
          // Archer Tower
          event.preventDefault();
          activateBuildMode(BuildingType.TOWER_ARCHER);
          return;
        }
        if (key === 'c') {
          // Cannon Tower
          event.preventDefault();
          activateBuildMode(BuildingType.TOWER_CANNON);
          return;
        }
        if (key === 'g') {
          // Magic Tower
          event.preventDefault();
          activateBuildMode(BuildingType.TOWER_MAGIC);
          return;
        }
        return;
      }

      // Main build menu category hotkeys (when tower submenu is NOT open)
      if (key === 'w') {
        // Wall
        event.preventDefault();
        activateBuildMode(BuildingType.WALL);
        return;
      }

      if (key === 'b') {
        // Barracks
        event.preventDefault();
        activateBuildMode(BuildingType.BARRACKS);
        return;
      }

      if (key === 'h') {
        // Hero Spawner
        event.preventDefault();
        activateBuildMode(BuildingType.HERO_SPAWNER);
        return;
      }

      if (key === 'e') {
        // Tech Building
        event.preventDefault();
        activateBuildMode(BuildingType.TECH_BUILDING);
        return;
      }

      if (key === 'm') {
        // Mine
        event.preventDefault();
        activateBuildMode(BuildingType.MINE);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [
    scene,
    isBuildModeActive,
    isBuildMenuOpen,
    isTowerSubmenuOpen,
    selectedUnitIds,
    activateBuildMode,
    deactivateBuildMode,
    closeBuildMenu,
    toggleBuildMenu,
    openTowerSubmenu,
    closeTowerSubmenu,
  ]);
}
